import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { motion } from 'framer-motion';
import { FaCode, FaRocket, FaPaintBrush, FaShoppingCart, FaComments, FaStar, FaPuzzlePiece, FaChartLine, FaBullhorn, FaTwitter, FaInstagram, FaBehance, FaArrowRight, FaCheck } from 'react-icons/fa';

const ServiceCard = ({ icon: Icon, title, description, features, techStack }) => {
  return (
    <motion.div
      className="group bg-white/5 backdrop-blur border border-white/10 p-8 rounded-3xl shadow-card hover:shadow-elevated transition-all duration-300 hover:-translate-y-2"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
    >
      <div className="w-16 h-16 bg-perspective-sage/15 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-perspective-sage/25 transition-colors">
        <Icon className="text-perspective-sage text-2xl" />
      </div>
      <h3 className="text-2xl font-bold mb-4 text-white">{title}</h3>
      <p className="text-perspective-cloud/85 mb-6 leading-relaxed">{description}</p>
      <h4 className="text-lg font-semibold mb-4 text-perspective-sage">Key Features:</h4>
      <ul className="space-y-3 mb-8">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center gap-3">
            <FaCheck className="text-perspective-sage text-sm flex-shrink-0" />
            <span className="text-perspective-cloud/80">{feature}</span>
          </li>
        ))}
      </ul>
      <h4 className="text-lg font-semibold mb-4 text-perspective-sage">Tech Stack:</h4>
      <div className="flex flex-wrap gap-3">
        {techStack.map((tech, index) => (
          <div key={index} className="flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-2 rounded-lg">
            <img src={tech.logo} alt={tech.name} className="w-6 h-6" />
            <span className="text-sm text-perspective-cloud/80 font-medium">{tech.name}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

const Services = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: FaCode,
      title: "Web Solutions",
      description: "Modern, responsive, and scalable web solutions designed to elevate your business. We build fast-loading, secure, and engaging websites tailored to your specific needs.",
      features: [
        "Custom Website Development",
        "Responsive Design",
        "SEO Optimization",
        "Performance Optimization",
        "Web Accessibility Standards Compliance",
      ],
      techStack: [
        { name: "React.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" },
        { name: "Next.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original-wordmark.svg" },
        { name: "TailwindCSS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original-wordmark.svg" },
        { name: "Firebase", logo: "https://www.vectorlogo.zone/logos/firebase/firebase-icon.svg" },
      ],
    },
    {
      icon: FaRocket,
      title: "SaaS Tools",
      description: "End-to-end development for SaaS applications, enabling businesses to scale effectively with cloud-powered tools. Our SaaS platforms are reliable, secure, and feature-rich.",
      features: [
        "Custom SaaS Development",
        "Cloud Integration",
        "API Development",
        "Subscription Management",
        "User Role & Permission Handling",
      ],
      techStack: [
        { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg" },
        { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
        { name: "MongoDB", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg" },
        { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg" },
      ],
    },
    {
      icon: FaChartLine,
      title: "Digital Marketing",
      description: "Comprehensive digital marketing strategies that drive traffic, engagement, and conversions for your business.",
      features: [
        "SEO & SEM",
        "Social Media Marketing",
        "Content Strategy",
        "Analytics & Reporting",
        "Campaign Management"
      ],
      techStack: [
        { name: "Google Analytics", logo: "https://www.vectorlogo.zone/logos/google_analytics/google_analytics-icon.svg" },
        { name: "Facebook Ads", logo: "https://www.vectorlogo.zone/logos/facebook/facebook-icon.svg" },
        { name: "HubSpot", logo: "https://www.vectorlogo.zone/logos/hubspot/hubspot-icon.svg" },
        { name: "Mailchimp", logo: "https://www.vectorlogo.zone/logos/mailchimp/mailchimp-icon.svg" },
      ],
    },
    {
      icon: FaBullhorn,
      title: "Brand Marketing",
      description: "Strategic brand development and marketing campaigns that build awareness and drive customer acquisition.",
      features: [
        "Brand Strategy",
        "Campaign Management",
        "Content Creation",
        "Brand Identity Design",
        "Market Research"
      ],
      techStack: [
        { name: "Figma", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg" },
        { name: "Canva", logo: "https://www.vectorlogo.zone/logos/canva/canva-icon.svg" },
        { name: "Adobe Creative", logo: "https://www.vectorlogo.zone/logos/adobe/adobe-icon.svg" },
        { name: "Hootsuite", logo: "https://www.vectorlogo.zone/logos/hootsuite/hootsuite-icon.svg" },
      ],
    },
    {
      icon: FaPaintBrush,
      title: "UX/UI Design",
      description: "Beautiful, user-centric designs that deliver outstanding user experiences. We ensure your digital products are intuitive, aesthetically pleasing, and aligned with your brand identity.",
      features: [
        "User Experience (UX) Design",
        "User Interface (UI) Design",
        "Prototyping & Wireframing",
        "Design Systems & Brand Identity",
        "Usability Testing",
      ],
      techStack: [
        { name: "Figma", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg" },
        { name: "Adobe XD", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/xd/xd-original.svg" },
        { name: "Sketch", logo: "https://www.vectorlogo.zone/logos/sketchapp/sketchapp-icon.svg" },
      ],
    },
    {
      icon: FaShoppingCart,
      title: "E-Commerce",
      description: "Craft powerful e-commerce platforms with seamless payment integrations and intuitive interfaces to maximize your sales and enhance the shopping experience.",
      features: [
        "Custom E-Commerce Development",
        "Payment Gateway Integration",
        "Inventory Management",
        "Product Recommendation Engines",
        "Performance Optimization",
      ],
      techStack: [
        { name: "WooCommerce", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/woocommerce/woocommerce-original.svg" },
        { name: "Stripe", logo: "https://www.vectorlogo.zone/logos/stripe/stripe-icon.svg" },
        { name: "React.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" },
      ],
    },
    {
      icon: FaComments,
      title: "AI Chatbots",
      description: "Revolutionize customer interactions with AI-powered chatbots that provide personalized and efficient customer support, available 24/7.",
      features: [
        "Custom AI Chatbot Development",
        "Natural Language Processing (NLP)",
        "Integration with CRMs",
        "Customizable Chat Flows",
        "Voice Assistant Integration",
      ],
      techStack: [
        { name: "TensorFlow", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tensorflow/tensorflow-original.svg" },
        { name: "OpenAI", logo: "https://www.vectorlogo.zone/logos/openai/openai-icon.svg" },
        { name: "Firebase", logo: "https://www.vectorlogo.zone/logos/firebase/firebase-icon.svg" },
      ],
    },
    {
      icon: FaPuzzlePiece,
      title: "No Code Solutions",
      description: "Empower your business with modern, intuitive no-code solutions that allow you to quickly build and manage responsive websites and applications without writing a single line of code.",
      features: [
        "Drag-and-Drop Website Builder",
        "Customizable Templates",
        "Real-time Editing",
        "Integration with Third-Party Services",
        "Scalable and Secure Solutions"
      ],
      techStack: [
        { name: "Framer", logo: "https://www.vectorlogo.zone/logos/framer/framer-icon.svg" },
        { name: "Webflow", logo: "https://www.vectorlogo.zone/logos/webflow/webflow-icon.svg" },
        { name: "Softr.io", logo: "https://cdn.brandfetch.io/idytCFzVcY/theme/dark/logo.svg?c=1dxbfHSJFAPEGdCLU4o5B" },
        { name: "TeleportHQ", logo: "https://cdn.brandfetch.io/id9g8jsAcx/theme/light/logo.svg?c=1dxbfHSJFAPEGdCLU4o5B" },
      ],
    },
  ];

  const stats = [
    { value: "120+", label: "Projects Completed" },
    { value: "98%", label: "Client Satisfaction" },
    { value: "5+", label: "Years of Experience" }
  ];

  const testimonials = [
    {
      name: "Martin Adler",
      role: "FirstCorp",
      content: "Working with PerspectiveHQ transformed our business. Their web solutions helped us increase our online presence and boost sales by 200%.",
      rating: 5
    },
    {
      name: "Thami Zola",
      role: "Startup X",
      content: "The e-commerce platform they built for us is exactly what we needed. Our customers love the seamless shopping experience.",
      rating: 5
    },
    {
      name: "Lindiwe Ndlovu",
      role: "GrowthCo",
      content: "Their AI chatbot solution has dramatically improved our customer service efficiency while maintaining a personal touch.",
      rating: 5
    }
  ];

  const processSteps = [
    {
      title: "Discovery",
      description: "Understanding your goals and requirements."
    },
    {
      title: "Planning",
      description: "Mapping out the strategy and deliverables."
    },
    {
      title: "Execution",
      description: "Bringing the plan to life with precision and efficiency."
    },
    {
      title: "Delivery",
      description: "Ensuring a seamless handover and ongoing support."
    }
  ];

  const faqs = [
    {
      question: "How long does a typical project take?",
      answer: "Project timelines vary based on scope and complexity. A simple website might take 4-6 weeks, while a complex SaaS platform could take 3-6 months. We'll provide detailed timelines during our initial consultation."
    },
    {
      question: "What is your pricing structure?",
      answer: "We offer flexible pricing models including project-based, hourly, and retainer options. Each solution is customized to your specific needs and budget. Contact us for a detailed quote."
    },
    {
      question: "Do you provide ongoing support?",
      answer: "Yes, we offer comprehensive maintenance and support packages to ensure your digital solutions continue to perform optimally after launch."
    },
    {
      question: "Can you work with existing systems?",
      answer: "Absolutely! We have extensive experience integrating with existing systems and can work with your current tech stack to enhance and improve functionality."
    }
  ];

  return (
    <div className="min-h-screen bg-perspective-carbon text-perspective-sand relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute inset-0 bg-grid-soft [background-size:28px_28px] opacity-20" />
      </div>

      <div className="relative z-10">
        <Navbar />
        <div className="h-16" />

        {/* Hero Section */}
        <section className="px-4 pt-12 pb-12 lg:pt-16 lg:pb-16">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sm font-semibold text-perspective-sage uppercase tracking-[0.08em] mb-4">What we offer</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                Services That Drive Growth
              </h1>
              <p className="text-lg sm:text-xl text-perspective-cloud/85 max-w-3xl mx-auto leading-relaxed">
                From websites to SaaS platforms, we deliver digital solutions that combine strategy, design, and technology to help your business thrive.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="px-4 py-12 bg-perspective-ink/60 backdrop-blur">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center bg-white/5 backdrop-blur border border-white/10 p-6 rounded-2xl"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <h3 className="text-4xl font-bold text-perspective-sage mb-3">{stat.value}</h3>
                  <p className="text-lg text-perspective-cloud/80">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="px-4 py-16 lg:py-20">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-semibold text-perspective-sage uppercase tracking-[0.08em] mb-4">Our expertise</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Comprehensive Digital Solutions</h2>
              <p className="text-lg text-perspective-cloud/80 max-w-2xl mx-auto">
                Every service is designed to deliver measurable results and exceptional user experiences.
              </p>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <ServiceCard key={index} {...service} />
              ))}
            </div>
          </div>
        </section>

        {/* Process Overview */}
        <section className="px-4 py-16 lg:py-20 bg-perspective-ink/60 backdrop-blur">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-semibold text-perspective-sage uppercase tracking-[0.08em] mb-4">How we work</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Our Proven Process</h2>
              <p className="text-lg text-perspective-cloud/80 max-w-2xl mx-auto">
                A systematic approach that ensures every project delivers exceptional results.
              </p>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {processSteps.map((step, index) => (
                <motion.div
                  key={index}
                  className="bg-white/5 backdrop-blur border border-white/10 p-6 rounded-2xl text-center"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="w-14 h-14 bg-perspective-sage/15 text-perspective-sage rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 border border-perspective-sage/20">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-white">{step.title}</h3>
                  <p className="text-perspective-cloud/80 text-sm">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="px-4 py-16 lg:py-20">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-semibold text-perspective-sage uppercase tracking-[0.08em] mb-4">Client success</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">What Our Clients Say</h2>
              <p className="text-lg text-perspective-cloud/80 max-w-2xl mx-auto">
                Don't just take our word for it — hear from our satisfied clients.
              </p>
            </motion.div>
            
            <div className="relative">
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white/5 backdrop-blur border border-white/10 p-10 rounded-3xl shadow-elevated max-w-4xl mx-auto"
              >
                <div className="flex items-center gap-6 mb-6">
                  <div className="w-16 h-16 bg-perspective-sage/15 rounded-full flex items-center justify-center border border-perspective-sage/20">
                    <span className="text-2xl font-bold text-perspective-sage">
                      {testimonials[activeTestimonial].name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">{testimonials[activeTestimonial].name}</h3>
                    <p className="text-perspective-cloud/80 text-lg">{testimonials[activeTestimonial].role}</p>
                  </div>
                </div>
                <p className="text-xl text-perspective-cloud/90 mb-6 leading-relaxed">"{testimonials[activeTestimonial].content}"</p>
                <div className="flex gap-1">
                  {[...Array(5)].map((_, index) => (
                    <FaStar key={index} className="text-perspective-sage text-lg" />
                  ))}
                </div>
              </motion.div>
              <div className="flex justify-center gap-3 mt-8">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === activeTestimonial ? 'bg-perspective-sage scale-125' : 'bg-white/20 hover:bg-white/40'
                    }`}
                    onClick={() => setActiveTestimonial(index)}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="px-4 py-16 lg:py-20 bg-perspective-ink/60 backdrop-blur">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <p className="text-sm font-semibold text-perspective-sage uppercase tracking-[0.08em] mb-4">Got questions?</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-perspective-cloud/80 max-w-2xl mx-auto">
                Common questions about our services and process.
              </p>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {faqs.map((faq, index) => (
                <motion.div 
                  key={index} 
                  className="bg-white/5 backdrop-blur border border-white/10 p-6 rounded-2xl hover:border-perspective-sage/30 transition-all duration-300"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <h3 className="text-xl font-bold mb-3 text-perspective-sage">{faq.question}</h3>
                  <p className="text-perspective-cloud/80 leading-relaxed">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-4 py-16 lg:py-20">
          <div className="max-w-6xl mx-auto rounded-3xl overflow-hidden bg-gradient-to-r from-perspective-carbon to-perspective-border text-white shadow-elevated border border-perspective-sage/15">
            <div className="p-10 md:p-12 text-center">
              <p className="text-sm uppercase tracking-[0.12em] text-perspective-mint mb-4">Ready to get started?</p>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">Transform Your Business Today</h2>
              <p className="text-lg text-perspective-mint/90 mb-8 max-w-2xl mx-auto">
                Let's discuss how our services can help you achieve your business goals and drive real results.
              </p>
              <button 
                onClick={() => navigate('/contact')}
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-perspective-sage text-perspective-carbon font-semibold hover:scale-105 transition-all duration-300 shadow-lg"
              >
                Get Started Today
                <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-perspective-carbon text-white border-t border-white/10">
          <div className="max-w-6xl mx-auto px-4 py-14">
            <div className="grid md:grid-cols-4 gap-8 mb-10">
              <div className="md:col-span-2">
                <img 
                  src="/perspectivelogo.png" 
                  alt="Perspective HQ" 
                  className="h-8 w-auto mb-4"
                />
                <p className="text-perspective-mint/90 max-w-xl mb-6">
                  Creating digital experiences that drive growth and innovation. We're passionate about building solutions that make a difference.
                </p>
                <div className="flex gap-3">
                  <a href="https://twitter.com/perspectivehq" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaTwitter size={16} />
                  </a>
                  <a href="https://instagram.com/theperspectivehq" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaInstagram size={16} />
                  </a>
                  <a href="https://www.behance.net/skaina" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaBehance size={16} />
                  </a>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-4">Navigate</h4>
                <div className="space-y-2 text-perspective-mint/90">
                  <button onClick={() => navigate('/work')} className="block hover:text-perspective-sage transition-colors">Work</button>
                  <button onClick={() => navigate('/services')} className="block hover:text-perspective-sage transition-colors">Services</button>
                  <button onClick={() => navigate('/about')} className="block hover:text-perspective-sage transition-colors">About</button>
                  <button onClick={() => navigate('/blog')} className="block hover:text-perspective-sage transition-colors">Blog</button>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-4">Contact</h4>
                <div className="space-y-2 text-perspective-mint/90">
                  <p>info@perspectivehq.co.za</p>
                  <p>+27 69 339 5619</p>
                  <p>Durban, South Africa</p>
                </div>
              </div>
            </div>
            <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-sm text-perspective-mint/80">
              <p>© 2021-2025 Perspective HQ. All rights reserved.</p>
              <div className="flex gap-4">
                <button onClick={() => navigate('/privacy-policy')} className="hover:text-perspective-sage transition-colors">Privacy Policy</button>
                <button onClick={() => navigate('/terms-of-service')} className="hover:text-perspective-sage transition-colors">Terms of Service</button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Services;
